import React from 'react';
import { Button, makeStyles, shorthands, mergeClasses } from '@fluentui/react-components';
import {
  ChevronLeft20Regular,
  ChevronRight20Regular,
  ChevronDoubleLeft20Regular,
  ChevronDoubleRight20Regular,
} from '@fluentui/react-icons';
import { useIsMobile } from '../hooks/useIsMobile';

interface PaginationProps {
  currentPage: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  maxPageButtons?: number;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '12px',
    ...shorthands.padding('16px', '0'),
    // The full control set is far wider than a phone; flex-end would clip
    // the leading buttons off the left edge. Center and tighten on mobile
    // (the range text is hidden + fewer page buttons render — see below).
    // Drop the vertical padding too — the footer ate a lot of screen height.
    '@media (max-width: 768px)': {
      justifyContent: 'center',
      gap: '4px',
      ...shorthands.padding('4px', '0'),
    },
  },
  rangeTextMobileHidden: {
    '@media (max-width: 768px)': {
      display: 'none',
    },
  },
  rangeText: {
    fontSize: '14px',
    color: '#242424',
    marginRight: '8px',
  },
  pageButton: {
    minWidth: '32px',
    height: '32px',
    ...shorthands.padding('0'),
    fontSize: '14px',
  },
  activePageButton: {
    backgroundColor: '#0078d4',
    color: '#ffffff',
    '&:hover': {
      backgroundColor: '#106ebe',
    },
  },
  navButton: {
    minWidth: '32px',
    height: '32px',
    ...shorthands.padding('0'),
  },
});

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalItems,
  itemsPerPage,
  onPageChange,
  maxPageButtons = 5,
}) => {
  const styles = useStyles();
  const isMobile = useIsMobile();
  // Fewer page buttons on phones so the row (with prev/next/first/last) fits
  // without clipping. 3 keeps current ± 1 visible.
  const effectiveMaxButtons = isMobile ? 3 : maxPageButtons;

  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  // Calculate which page numbers to show
  const getPageNumbers = () => {
    const pages: number[] = [];

    if (totalPages <= effectiveMaxButtons) {
      // Show all pages if total is less than max
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show pages around current page
      let startPage = Math.max(1, currentPage - Math.floor(effectiveMaxButtons / 2));
      let endPage = Math.min(totalPages, startPage + effectiveMaxButtons - 1);

      // Adjust if we're near the end
      if (endPage - startPage < effectiveMaxButtons - 1) {
        startPage = Math.max(1, endPage - effectiveMaxButtons + 1);
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  const handleFirstPage = () => onPageChange(1);
  const handlePrevPage = () => onPageChange(Math.max(1, currentPage - 1));
  const handleNextPage = () => onPageChange(Math.min(totalPages, currentPage + 1));
  const handleLastPage = () => onPageChange(totalPages);

  if (totalItems === 0) {
    return null;
  }

  return (
    <div className={styles.container}>
      <span className={mergeClasses(styles.rangeText, styles.rangeTextMobileHidden)}>
        Showing {startItem} - {endItem} of {totalItems}
      </span>

      <Button
        appearance="subtle"
        icon={<ChevronDoubleLeft20Regular />}
        onClick={handleFirstPage}
        disabled={currentPage === 1}
        className={styles.navButton}
        aria-label="First page"
      />

      <Button
        appearance="subtle"
        icon={<ChevronLeft20Regular />}
        onClick={handlePrevPage}
        disabled={currentPage === 1}
        className={styles.navButton}
        aria-label="Previous page"
      />

      {pageNumbers.map((page) => (
        <Button
          key={page}
          appearance={page === currentPage ? 'primary' : 'subtle'}
          onClick={() => onPageChange(page)}
          className={mergeClasses(styles.pageButton, page === currentPage && styles.activePageButton)}
        >
          {page}
        </Button>
      ))}

      <Button
        appearance="subtle"
        icon={<ChevronRight20Regular />}
        onClick={handleNextPage}
        disabled={currentPage === totalPages}
        className={styles.navButton}
        aria-label="Next page"
      />

      <Button
        appearance="subtle"
        icon={<ChevronDoubleRight20Regular />}
        onClick={handleLastPage}
        disabled={currentPage === totalPages}
        className={styles.navButton}
        aria-label="Last page"
      />
    </div>
  );
};

export default Pagination;
